'use client';

import { type ComponentProps, forwardRef, useState, useEffect, type PropsWithChildren } from 'react';
import SearchIcon from '../_assets/icons/search.svg';
import KeyCommandIcon from '../_assets/icons/keyCommand.svg';

import { tv } from '../_lib/utils';
import { Anchor } from '../Anchor';
import { Button, ButtonLink } from '../Inputs/Button';
import { Popup } from '../Popup';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import type { IconCardProps } from '../IconCard';
import { CrossLinks, type CrossLinksProps } from '../CrossLinks';
import { Text } from '../Text';
import { AlgoliaSearch, searchClient, useAutocomplete } from '../AlgoliaSearch';
import { Configure } from 'react-instantsearch';

const styles = tv({
	slots: {
		base: 'transition-all duration-normal ease-smooth w-full flex flex-row items-center p-[0.38rem] border focus-within:border-purple-100/80 focus-within:text-purple-100/80 hover:border-purple-100/60 hover:text-purple-100/80 bg-white text-purple-100/70 border-purple-100/40',
		input: 'w-full disabled:bg-transparent disabled:pointer-events-none text-body-sm pr-2',
	},
	variants: {
		type: {
			primary: {
				base: 'max-w-[26rem] pl-[1.13rem] rounded-[2.06rem]',
			},
			'primary-search': {
				base: 'max-w-[26rem] pl-[1.13rem] rounded-[2.06rem]',
			},
			secondary: {
				base: 'pl-3 rounded-[0.25rem]',
			},
		},
		hasValue: {
			true: {
				base: 'border-purple-100/60 text-purple-100/80',
			},
		},
		disabled: {
			true: {
				base: 'border-grey-60 bg-grey-20 text-grey-60',
			},
		},
	},
});

type LabelProps = {
	hasValue?: boolean;
	disabled?: boolean;
	type?: 'primary' | 'secondary' | 'primary-search';
};

export type SearchProps = Omit<ComponentProps<'input'>, 'name' | 'id' | 'ref'> & {
	id: string;
	name: string;
	results: any[];
} & LabelProps;

export const Search = forwardRef<HTMLInputElement, SearchProps>(function Search({ className, hasValue, disabled, ...props }, ref) {
	const [popupOpen, setPopupOpen] = useState(false);
	const type = props.type ?? 'primary';
	const { base } = styles({ type, hasValue, disabled });

	useEffect(() => {
		if (type !== 'primary-search') {
			const keyDownHandler = event => {
				if (event.code === 'Escape') {
					event.preventDefault();
					setPopupOpen(false);
				}

				if (event.metaKey && event.keyCode === 75) {
					event.preventDefault();
					setPopupOpen(true);
				}
			};

			document.addEventListener('keydown', keyDownHandler);

			return () => {
				document.removeEventListener('keydown', keyDownHandler);
			};
		}
	}, []);

	const [recentSearchesIDs, setRecentSearchesIDs] = useState<string[] | undefined>();
	const [recentSearches, setRecentSearches] = useState<IconCardProps[]>();

	const saveNewRecentSearch = id => saveRecentSearchesFromLocalStorage(id, setRecentSearchesIDs);

	useEffect(() => {
		if (recentSearchesIDs !== undefined) {
			const recentSearches = async () => await recentSearchesFetch(recentSearchesIDs);
			recentSearches().then(recentSearches => setRecentSearches(recentSearches));
		} else {
			const ids = getRecentSearchesFromLocalStorage();
			if (ids) {
				setRecentSearchesIDs(ids);
				const recentSearches = async () => await recentSearchesFetch(ids);
				recentSearches().then(recentSearches => setRecentSearches(recentSearches));
			} else {
				setRecentSearches(undefined);
			}
		}
	}, [recentSearchesIDs]);

	return (
		searchClient && (
			<>
				{type === 'primary-search' && (
					<AlgoliaWrapper hitsPerPage={100}>
						<SearchPage
							recentSearches={recentSearches}
							saveNewRecentSearch={saveNewRecentSearch}
							hasValue={hasValue}
							disabled={disabled}
							ref={ref}
							{...props}
						/>
					</AlgoliaWrapper>
				)}

				{type === 'primary' && (
					<Popup
						layout='search'
						triggerButton={
							<div className={`block relative w-full hover:cursor-pointer ${base()} ${className || ''}`}>
								<SearchInput hasValue={hasValue} disabled={true} ref={ref} {...props} />
								<Button
									parent='Search'
									disabled={disabled}
									styleSize='medium'
									iconOnly
									className='px-[0.9125rem] [&_svg]:h-[0.875rem] h-fit w-fit text-purple-100'
								>
									<SearchIcon />
								</Button>
							</div>
						}
					>
						<AlgoliaWrapper hitsPerPage={3}>
							<SearchPopup
								recentSearches={recentSearches}
								saveNewRecentSearch={saveNewRecentSearch}
								hasValue={hasValue}
								disabled={disabled}
								ref={ref}
								{...props}
							/>
						</AlgoliaWrapper>
					</Popup>
				)}

				{type === 'secondary' && (
					<Popup
						open={popupOpen}
						setOpen={setPopupOpen}
						layout='search'
						triggerButton={
							<div className={`block relative w-full hover:cursor-pointer ${base()} ${className || ''}`} onClick={() => setPopupOpen(true)}>
								<SearchInput disabled={true} {...props} />
								<kbd className='text-purple-100/50 pr-2 [&_svg]:w-5 [&_svg]:h-5 pointer-events-none'>
									<KeyCommandIcon />
								</kbd>
							</div>
						}
					>
						<AlgoliaWrapper hitsPerPage={3}>
							<SearchPopup
								recentSearches={recentSearches}
								saveNewRecentSearch={saveNewRecentSearch}
								hasValue={hasValue}
								disabled={disabled}
								ref={ref}
								{...props}
							/>
						</AlgoliaWrapper>
					</Popup>
				)}
			</>
		)
	);
});

export const SearchInput = forwardRef<HTMLInputElement, SearchProps>(function SearchInput({ hasValue, disabled, ...props }, ref) {
	const { input } = styles({ hasValue, disabled });

	return <input ref={ref} className={input({ className: props.className })} {...props} type={props.type || 'text'} disabled={disabled} />;
});

export const SearchPopup = forwardRef<
	HTMLInputElement,
	SearchProps & { recentSearches?: IconCardProps[]; saveNewRecentSearch(id: string): void }
>(function SearchPopup({ hasValue, disabled, recentSearches, saveNewRecentSearch, ...props }, ref) {
	const { base } = styles({ type: 'primary', hasValue, disabled });

	const { indices, refine } = useAutocomplete();

	const [value, setValue] = useState<string>('');
	const [onSubmit, setOnSubmit] = useState<boolean>(false);
	const [searchResultOnSubmit, setSearchResultOnSubmit] = useState<IconCardProps[] | undefined>();

	useEffect(() => {
		if (onSubmit) {
			setOnSubmit(false);

			if (value.length > 0) {
				const result: IconCardProps[] = [];

				if (Array.isArray(indices[0].hits)) {
					for (const hit of indices[0].hits) {
						if (hit['guideName'] && hit['guideURL']) {
							result.push({
								id: hit['objectID'] ?? undefined,
								title: hit['guideName'],
								copy: hit['guideDescription'],
								href: hit['guideURL'],
							});
						}
					}
				}
				setSearchResultOnSubmit(result);
			} else {
				setSearchResultOnSubmit(undefined);
			}
		}
	}, [indices, value]);

	useEffect(() => {
		if (onSubmit) {
			refine(value);
		}
	}, [onSubmit]);

	const searchResults = searchResultOnSubmit && searchResultOnSubmit.length > 0 ? searchResultOnSubmit : recentSearches;
	const searchResultType = searchResultOnSubmit && searchResultOnSubmit.length > 0;
	const moreResults = indices[0].results.nbHits > 3;

	return (
		<>
			<div className={`block relative w-full max-w-none ${base()} `}>
				<SearchInput
					className={`py-3 px-2 w-full`}
					hasValue={hasValue}
					disabled={disabled}
					ref={ref}
					onChange={e => {
						setValue(e.target.value);
					}}
					onKeyDown={event => {
						if (event.key === 'Enter') {
							setOnSubmit(true);
						}
					}}
					{...props}
				/>
				<Button
					parent='Search'
					disabled={disabled}
					styleSize='medium'
					iconOnly
					className='px-[0.9125rem] [&_svg]:h-[0.875rem] h-fit w-fit text-purple-100'
					onClick={() => setOnSubmit(true)}
				>
					<SearchIcon />
				</Button>
			</div>
			<div className={`mt-8`}>
				<>
					{/* // todo need review for multilingual */}
					<Text as='span' className='opacity-80 block pb-2' styleType='label-sm'>
						{searchResultType ? 'Search Results' : recentSearches ? 'Recent Searches' : 'No Results'}
					</Text>
					{searchResults && searchResults.length > 0 && (
						<ul className={`grid gap-3`}>
							{searchResults.map(({ copy, href, title, id }, index) => (
								<li
									className={`p-6 rounded-[1.125rem] relative gradient-border before:rounded-[1.125rem] before:border before:border-transparent before:[background:var(--gradient-card-border)_border-box] before:pointer-events-none before:transition-opacity before:duration-normal before:ease-smooth after:content-[""] after:border after:absolute after:top-0 after:bottom-0 after:left-0 after:right-0 after:rounded-[1.125rem] after:pointer-events-none after:border-accordion-item-hover-border/60 after:opacity-0 after:transition-opacity after:duration-normal after:ease-smooth hover:before:opacity-0 hover:after:opacity-100`}
									key={`search-result-${index}-${title}`}
								>
									<Anchor
										className={`before:content-[""] before:left-0 before:bottom-0 before:right-0 before:top-0 before:absolute`}
										href={href}
										onClick={() => id && saveNewRecentSearch(id)}
									>
										<Text as='h2' className={`mb-2`} styleType='heading-5xs'>
											{title}
										</Text>
										{copy && <p className={`text-body-sm opacity-80`}>{copy}</p>}
									</Anchor>
								</li>
							))}
						</ul>
					)}
					{moreResults && searchResultType && (
						<div className='mt-3' data-brand='link' data-theme='dark'>
							<ButtonLink parent='Search' href={'/support/search?query=' + value} styleType={'secondary'} styleSize={'medium'}>
								{`See all ${indices[0].results.nbHits} results`}
							</ButtonLink>
						</div>
					)}
				</>
			</div>
		</>
	);
});

export const SearchPage = forwardRef<
	HTMLInputElement,
	SearchProps & { recentSearches?: IconCardProps[]; saveNewRecentSearch(id: string): void }
>(function SearchPage({ hasValue, disabled, recentSearches, saveNewRecentSearch, ...props }, ref) {
	const path = usePathname();
	const router = useRouter();
	const searchParams = useSearchParams();
	const query = searchParams.get('query');

	const { base } = styles({ type: 'primary', hasValue, disabled });

	const { indices, refine } = useAutocomplete();

	const [value, setValue] = useState<string>(query ?? '');

	useEffect(() => {
		refine(value);
		router.replace(path + `${value ? '?query=' + value : ''}`);
	}, [value]);

	const result: IconCardProps[] = [];

	if (Array.isArray(indices[0].hits)) {
		for (const hit of indices[0].hits) {
			if (hit['guideName'] && hit['guideURL']) {
				result.push({
					id: hit['objectID'] ?? undefined,
					title: hit['guideName'] ?? undefined,
					copy: hit['guideDescription'] ?? undefined,
					href: hit['guideURL'] ?? undefined,
				});
			}
		}
	}

	const searchResults = result.length > 0 && value !== '' ? result : undefined;

	const crossLinksProps: CrossLinksProps = {
		label: searchResults ? 'Search Results' : recentSearches ? 'Recent Searches' : 'No Results', // todo need review for multilingual
		cards: searchResults ? searchResults : recentSearches,
	};

	return (
		<>
			<div className={` relative w-full mt-6 ${base()} block`}>
				<SearchInput
					value={value}
					className={`py-3 px-2 mr-3 w-full`}
					hasValue={hasValue}
					disabled={disabled}
					ref={ref}
					{...props}
					onChange={e => {
						setValue(e.target.value);
					}}
				/>
			</div>
			<div className='no-nested-containers w-full'>
				<CrossLinks {...crossLinksProps} onCardClick={saveNewRecentSearch} />
			</div>
		</>
	);
});

export function AlgoliaWrapper({ children, hitsPerPage }: PropsWithChildren & { hitsPerPage?: number }) {
	const language = usePathname().split('/')[1] ?? 'en-GB';

	return (
		<AlgoliaSearch indexName='guide'>
			<Configure hitsPerPage={hitsPerPage} filters={language.length ? `language:"${language}"` : undefined} />
			{children}
		</AlgoliaSearch>
	);
}

async function recentSearchesFetch(recentSearchesIDs: string[]) {
	const request = {
		indexName: 'guide',
		browseParams: {
			query: '',
			filters: `objectID:${recentSearchesIDs.join(' OR ')}`,
			hitsPerPage: recentSearchesIDs.length,
		},
	};

	const response = await fetch(`/api/search/browseFetch?data=${encodeURIComponent(JSON.stringify(request))}`);

	if (response.ok) {
		const data = await response.json();
		const result = data?.hits as unknown as SearchItemResponseType[] | undefined | null;

		if (result && Array.isArray(result) && result.length > 0) {
			const recentSearchesResult: IconCardProps[] = [];
			for (const hit of result) {
				if (hit['guideName'] && hit['guideURL']) {
					recentSearchesResult.push({
						id: hit['objectID'] ?? undefined,
						title: hit['guideName'] ?? undefined,
						copy: hit['guideDescription'] ?? undefined,
						href: hit['guideURL'] ?? undefined,
					});
				}
			}
			if (recentSearchesResult.length > 0) {
				return recentSearchesResult;
			}
		}
	} else {
		console.error('Request failed:', response.status);
	}
	return undefined;
}

type SearchItemResponseType = {
	guideName: string;
	guideDescription: string;
	guideURL: string;
	objectID: string;
};

function getParsedValue(key: string) {
	const notParsedValue = localStorage.getItem(key);
	const parsedValue = notParsedValue ? (JSON.parse(notParsedValue) as string) : null;

	return parsedValue;
}

function getRecentSearchesFromLocalStorage() {
	const recentSearch1 = getParsedValue('recentSearch1');
	const recentSearch2 = getParsedValue('recentSearch2');

	const recentSearches: string[] = [];

	if (recentSearch1) {
		recentSearches.push(recentSearch1);
	}
	if (recentSearch2) {
		recentSearches.push(recentSearch2);
	}

	return recentSearches.length > 0 ? recentSearches : undefined;
}

function saveRecentSearchesFromLocalStorage(newValue: string, setRecentSearchesIDs: (value: string[] | undefined) => void) {
	let newRecentSearch1: string | null;
	let newRecentSearch2: string | null;

	const oldRecentSearch1 = getParsedValue('recentSearch1');
	const oldRecentSearch2 = getParsedValue('recentSearch2');

	if (newValue !== oldRecentSearch1 && newValue !== oldRecentSearch2) {
		if (newValue) {
			newRecentSearch1 = newValue;
			newRecentSearch2 = getParsedValue('recentSearch1');
			localStorage.clear();
		} else {
			newRecentSearch1 = getParsedValue('recentSearch1');
			newRecentSearch2 = getParsedValue('recentSearch2');
		}

		const newRecentSearchersList: string[] = [];

		if (newRecentSearch1) {
			localStorage.setItem('recentSearch1', JSON.stringify(newRecentSearch1));
			newRecentSearchersList.push(newRecentSearch1);
		}

		if (newRecentSearch2) {
			localStorage.setItem('recentSearch2', JSON.stringify(newRecentSearch2));
			newRecentSearchersList.push(newRecentSearch2);
		}

		if (newRecentSearchersList.length > 0) {
			setRecentSearchesIDs(newRecentSearchersList);
		} else {
			setRecentSearchesIDs(undefined);
		}
	}
}
